/* eslint-disable */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Loader2, Download, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const CAT = { materiale: "Materiale", manopera: "Manoperă", transport: "Transport", altele: "Altele" };

export default function PublicOffer() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t");
  const presetAction = params.get("a");
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/offers/${id}?token=${encodeURIComponent(token)}`);
      setOffer(r.data);
    } catch (e) {
      setError("Link invalid sau ofertă inexistentă.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (token) { load(); } else { setError("Link invalid."); setLoading(false); } }, [id, token]);

  const respond = async (action) => {
    setBusy(action);
    try {
      const r = await axios.post(`${API}/public/offers/${id}/respond`, { token, action });
      setOffer({ ...offer, status: r.data.status });
      toast.success(action === "accept" ? "Ai acceptat oferta. Mulțumim!" : "Ai refuzat oferta.");
    } catch (e) {
      toast.error("A apărut o eroare. Încearcă din nou.");
    } finally {
      setBusy("");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-[#94A3B8]">Se încarcă…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="text-center"><X size={40} className="mx-auto text-[#EF4444]" /><p className="mt-4 font-semibold">{error}</p></div>
    </div>
  );

  const t = offer.totals || {};
  const decided = offer.status === "accepted" || offer.status === "refused";

  return (
    <div className="min-h-screen bg-[#0B111D] py-8 px-4" data-testid="public-offer">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <img src="/brand/logo-icon.png" alt="IronOfferAI" className="w-9 h-9 rounded-lg" />
          <span className="font-display font-extrabold text-lg">IronOfferAI</span>
        </div>

        <div className="bg-[#0B111D] border border-[#1E293B]">
          {/* Company */}
          <div className="bg-[#111C30] text-white p-6">
            <div className="text-[11px] uppercase tracking-widest text-white/60">Ofertă {offer.offer_number}</div>
            <h1 className="font-display text-2xl font-extrabold mt-1">{offer.project_title}</h1>
            <p className="text-sm text-white/70 mt-2">de la <b>{offer.company?.name || "Furnizor"}</b></p>
          </div>

          <div className="p-6">
            <p className="text-sm text-[#94A3B8]">Stimate {offer.client_name},</p>
            {offer.ai_intro && <p className="text-sm leading-relaxed mt-3">{offer.ai_intro}</p>}

            {/* Items */}
            <table className="w-full text-sm mt-6 border-t border-[#1E293B]">
              <thead><tr className="text-left text-[#94A3B8] text-xs uppercase">
                <th className="py-2">Descriere</th><th className="py-2 text-right">Cant.</th><th className="py-2 text-right">Preț</th><th className="py-2 text-right">Total</th>
              </tr></thead>
              <tbody>
                {(offer.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-[#1E293B]">
                    <td className="py-2">{it.description}</td>
                    <td className="py-2 text-right font-mono-plex">{(it.quantity ?? 1)} {it.unit}</td>
                    <td className="py-2 text-right font-mono-plex">{(it.unit_price ?? 0).toFixed(2)}</td>
                    <td className="py-2 text-right font-mono-plex">{((it.quantity ?? 1) * (it.unit_price ?? 0)).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Total */}
            <div className="mt-4 bg-[#00BFFF] text-white p-4 flex items-center justify-between">
              <span className="font-display font-bold">TOTAL (cu TVA {offer.vat_percent}%)</span>
              <span className="font-display font-extrabold text-xl" data-testid="public-total">{(t.total ?? 0).toFixed(2)} RON</span>
            </div>

            {/* PDF */}
            <a href={`${API}/public/offers/${id}/pdf?token=${encodeURIComponent(token)}`} target="_blank" rel="noreferrer"
               className="mt-4 inline-flex items-center gap-2 text-sm text-[#E2E8F0] underline" data-testid="public-pdf-link">
              <Download size={15} /> Descarcă oferta în PDF
            </a>

            {/* Actions / status */}
            <div className="mt-8 border-t border-[#1E293B] pt-6">
              {decided ? (
                <div className={`p-5 text-center ${offer.status === "accepted" ? "bg-[#0E2A1A] text-[#22C55E]" : "bg-[#2A1416] text-[#EF4444]"}`} data-testid="public-decided">
                  {offer.status === "accepted" ? <Check size={28} className="mx-auto" /> : <X size={28} className="mx-auto" />}
                  <p className="font-display font-bold text-lg mt-2">{offer.status === "accepted" ? "Ofertă acceptată" : "Ofertă refuzată"}</p>
                  <p className="text-sm mt-1 opacity-80">{offer.status === "accepted" ? `${offer.company?.name || "Furnizorul"} a fost notificat și te va contacta.` : "Mulțumim pentru răspuns."}</p>
                </div>
              ) : (
                <>
                  <p className="text-center text-sm text-[#94A3B8] mb-4">Răspunde la această ofertă:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => respond("accept")} disabled={!!busy}
                      className={`flex items-center justify-center gap-2 py-4 font-bold text-white bg-[#22C55E] hover:opacity-90 disabled:opacity-50 ${presetAction === "accept" ? "ring-4 ring-[#22C55E]/30" : ""}`} data-testid="accept-btn">
                      {busy === "accept" ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />} Acceptă oferta
                    </button>
                    <button onClick={() => respond("refuse")} disabled={!!busy}
                      className={`flex items-center justify-center gap-2 py-4 font-bold border border-[#1E293B] hover:border-[#EF4444] hover:text-[#EF4444] disabled:opacity-50 ${presetAction === "refuse" ? "ring-4 ring-[#EF4444]/20" : ""}`} data-testid="refuse-btn">
                      {busy === "refuse" ? <Loader2 size={18} className="animate-spin" /> : <X size={18} />} Refuză oferta
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Contact */}
            {(offer.company?.phone || offer.company?.email) && (
              <p className="text-xs text-[#94A3B8] mt-6 text-center">
                Contact: {offer.company?.phone} {offer.company?.email && `· ${offer.company.email}`}
              </p>
            )}
          </div>
        </div>

        <p className="text-xs text-[#94A3B8] mt-4 text-center flex items-center justify-center gap-1">
          <ShieldCheck size={13} /> Ofertă securizată prin IronOfferAI · ironofferai.com
        </p>
      </div>
    </div>
  );
}
